import React, { useState } from 'react';
import { ArrowRight, Fish, Loader2, AlertCircle } from 'lucide-react';
import {
  ROOT_DOMAIN, isExplicitOrigin, saveWorkspaceOrigin, workspaceOriginFor,
} from '../config/runtime';

/**
 * First screen of the store app: which company is this phone signing in to?
 *
 * A browser knows its workspace from the address bar (`varma.aquaerp.in`); the
 * app is one download for every company, so it asks once and remembers. The
 * answer is verified against the same public lookup the website's workspace
 * finder uses, so a typo is caught here rather than as a baffling login error.
 * A developer URL (`http://192.168.1.20:3000`) skips the lookup — a laptop has
 * no domain mapping — and only has to answer at all.
 */
export default function NativeWorkspace() {
  const [value, setValue] = useState('');
  const [state, setState] = useState({ status: 'idle', message: '' });

  const origin = workspaceOriginFor(value);
  const explicit = isExplicitOrigin(value);

  const submit = async (e) => {
    e.preventDefault();
    if (!origin) return;
    setState({ status: 'checking', message: '' });
    let host = '';
    try {
      host = new URL(origin).host;
    } catch {
      setState({ status: 'error', message: 'That does not look like a workspace address.' });
      return;
    }
    try {
      const res = await fetch(`${origin}/api/public/check-workspace?host=${encodeURIComponent(host)}`);
      if (!explicit && res.status === 404) {
        setState({ status: 'error', message: `No workspace called "${value.trim()}". Check the spelling, or ask your administrator for your company code.` });
        return;
      }
      if (!explicit && !res.ok) {
        setState({ status: 'error', message: `The workspace could not be checked right now (HTTP ${res.status}). Try again in a moment.` });
        return;
      }
    } catch {
      setState({ status: 'error', message: `Cannot reach ${host}. Check your internet connection and the company code.` });
      return;
    }
    saveWorkspaceOrigin(origin);
    // Reload so every module captures the new API base at import time.
    window.location.replace('/');
  };

  return (
    <div
      className="flex min-h-[100dvh] flex-col justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 px-5"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="mx-auto w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 shadow-sm">
            <Fish className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Aqua ERP</h1>
          <p className="mt-2 text-sm text-slate-600">Enter your company code to continue.</p>
        </div>

        <form onSubmit={submit} noValidate className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <label htmlFor="workspace" className="mb-2 block text-sm font-medium text-slate-700">Company code</label>
          <div className="flex items-stretch overflow-hidden rounded-xl border border-slate-300 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100">
            <input
              id="workspace"
              autoFocus
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="url"
              enterKeyHint="go"
              value={value}
              onChange={(e) => { setValue(e.target.value); setState({ status: 'idle', message: '' }); }}
              placeholder="your-company"
              className="min-w-0 flex-1 bg-transparent px-4 py-3 text-base outline-none placeholder:text-slate-400"
            />
            {!value.includes('.') && !explicit ? (
              <span className="flex select-none items-center border-l border-slate-200 bg-slate-50 px-3 text-sm text-slate-500">
                .{ROOT_DOMAIN}
              </span>
            ) : null}
          </div>

          <div className="mt-2 min-h-[1.25rem] text-sm">
            {state.status === 'error' ? (
              <span className="inline-flex items-start gap-1.5 text-red-600">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {state.message}
              </span>
            ) : (
              <span className="text-xs text-slate-400">
                The first part of the address you use in the browser — e.g. <b>acme</b> for acme.{ROOT_DOMAIN}.
              </span>
            )}
          </div>

          <button
            type="submit"
            disabled={!origin || state.status === 'checking'}
            className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 font-medium text-white transition hover:bg-blue-700 disabled:opacity-40"
          >
            {state.status === 'checking' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Continue
            {state.status !== 'checking' ? <ArrowRight className="h-4 w-4" /> : null}
          </button>
        </form>
      </div>
    </div>
  );
}
